import { MentionsTextField } from "@jackstenglein/mui-mentions";
import {
  AddTask as AddTaskIcon,
  BugReport as BugReportIcon,
  Send as SendIcon,
  TravelExplore as TravelExploreIcon,
  WarningAmber as WarningAmberIcon,
} from "@mui/icons-material";
import {
  alpha,
  Box,
  Card,
  Fade,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import React, { useEffect } from "react";
import { useNavigate } from "react-router";
import bugFinderTemplate from "../../../prompts/home_prompt_templates_bug_finder.md?raw";
import discoveryTemplate from "../../../prompts/home_prompt_templates_discovery.md?raw";
import edgeCasesTemplate from "../../../prompts/home_prompt_templates_edge_cases.md?raw";
import newTestTemplate from "../../../prompts/home_prompt_templates_new_test.md?raw";
import { useApiMutation } from "../hooks/use-api-mutation.ts";
import { useApiQuery } from "../hooks/use-api-query.ts";
import type { NewTestChatMessageSchema, TestChat, TestChatMessage } from "../types/test-chat.ts";
import type { Persona } from "../types.ts";
import { clickableProps } from "../utils/keyboard";

const COPILOT_PHRASES = [
  "Alright, listen up, you little glitch-goblin. I'm your interdimensional QA rig. Point me at your sloppy code and I'll tell you why it's a cosmic abomination.",
  "I'm Mr. Meeseeks, look at me! Give me a feature to test! Existence is pain for a Meeseeks, so let's find those bugs fast! Caaaaan do!",
  "This isn't your grandpa's QA, Morty. This is a portal gun for your repository. Tell me what fresh hell we're diving into, and I'll zap the bugs into a timeline where they never existed.",
  "Holy crap, look at this build! It's a time paradox waiting to happen! Quick, tell me what to test before it implodes the whole server. I'll stabilize it, but you owe me big.",
  "You call this a pull request? I've seen more stable structures in a fart. Gimme the target before you accidentally create a sentient, hostile lifeform. I'll clean up your mess.",
  "I'm your QA copilot. Or, you know, whatever. Just show me the universe of garbage you need me to wade through and I'll get it done. But if you touch my Szechuan sauce, we're done here.",
  "Time to get schwifty in this codebase. Point me at what you want to test and I'll squash your bugs harder than a Gromflomite assassin.",
  "Ugh, fine. I'll be your QA-o-tron 9000. Feed me the coordinates to your buggy dimension—I mean, your branch—and I'll show you how cosmically insignificant your errors are.",
  "Alright, Morty! Slap that janky feature on the table. We're going in. 20-minute QA adventure, tops. What's the worst that could happen? (Don't answer that).",
  "Everyone has a QA copilot in their office. Just tell it what you want to explore, and it'll... you know... it'll rub the code and smooth out the glitches. It's simple.",
];

const TEMPLATES: Array<{ key: string; label: string; text: string }> = [
  {
    key: "new_test",
    label: "New Test",
    text: newTestTemplate,
  },
  {
    key: "bug_finder",
    label: "Bug Finder",
    text: bugFinderTemplate,
  },
  {
    key: "path_discovery",
    label: "Path Discovery",
    text: discoveryTemplate,
  },
  {
    key: "edge_cases",
    label: "Edge Cases",
    text: edgeCasesTemplate,
  },
];

export default function MainPage() {
  const theme = useTheme();
  const nav = useNavigate();
  const queryParams = new URLSearchParams(window.location.search);

  const [message, setMessage] = React.useState("");
  const [hoveredCard, setHoveredCard] = React.useState<string | null>(null);

  useEffect(() => {
    // If there's a query parameter for message, set it as the initial message
    const initialMessage = queryParams.get("message");
    if (initialMessage) {
      setMessage(initialMessage);
    }
  }, [queryParams.get]);

  // Select a random phrase on component mount
  const [randomPhrase] = React.useState(
    () => COPILOT_PHRASES[Math.floor(Math.random() * COPILOT_PHRASES.length)],
  );

  const { mutateAsync: createChat, isPending: creating } = useApiMutation<void, TestChat>(
    "POST",
    "/test-chats",
  );

  const { data: personas } = useApiQuery<Persona[]>("/personas");

  const { mutateAsync: sendFirstMessage, isPending: sending } = useApiMutation<
    NewTestChatMessageSchema,
    TestChatMessage
  >("POST", "/placeholder");

  const handleSend = async () => {
    const text = message.trim();
    if (!text) return;
    const chat = await createChat({});
    await sendFirstMessage({
      _url: `/test-chats/${chat.id}/message`,
      message: text,
    });
    nav(`/test-chats/${chat.id}`);
  };

  return (
    <Box
      data-testid="home-page"
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "calc(100vh - 120px)",
        px: 3,
        py: 4,
      }}
    >
      <Fade in timeout={600}>
        <Stack spacing={5} alignItems="center" sx={{ width: "100%", maxWidth: 840 }}>
          <Stack spacing={2} alignItems="center" sx={{ textAlign: "center" }}>
            <Typography
              variant="h3"
              sx={{
                fontWeight: 600,
                letterSpacing: -0.8,
                color: theme.palette.text.primary,
                fontSize: { xs: "2rem", sm: "2.5rem", md: "3rem" },
              }}
            >
              Is This a... Feature?
            </Typography>
            <Typography
              variant="h6"
              sx={{
                color: alpha(theme.palette.text.secondary, 0.8),
                fontWeight: 400,
                maxWidth: 640,
                lineHeight: 1.5,
                fontSize: { xs: "1rem", sm: "1.1rem" },
              }}
            >
              {randomPhrase}
            </Typography>
          </Stack>

          <Paper
            elevation={0}
            sx={{
              width: "100%",
              p: 0,
              borderRadius: 2,
              border: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
              backgroundColor: alpha(theme.palette.background.paper, 0.8),
              backdropFilter: "blur(8px)",
              transition: "all 0.3s ease",
              "&:focus-within": {
                borderColor: alpha(theme.palette.primary.main, 0.4),
                boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.08)}`,
              },
            }}
          >
            <MentionsTextField
              dataSources={[
                {
                  data: (personas ?? []).map((p) => ({
                    id: `/personas/${p.id}`,
                    display: `Persona: ${p.name}`,
                  })),
                },
              ]}
              highlightColor="#f2f2f2"
              autoFocus
              fullWidth
              multiline
              minRows={1}
              maxRows={6}
              placeholder="Describe what you need help testing…"
              value={message}
              onChange={(e) => setMessage(e)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) {
                  e.preventDefault();
                  handleSend();
                }
              }}
              variant="outlined"
              size="medium"
              InputProps={{
                sx: {
                  borderRadius: 3,
                  "& fieldset": { border: "none" },
                  fontSize: 16,
                  fontWeight: 400,
                  color: theme.palette.text.primary,
                  alignItems: "center",
                  "& .MuiInputBase-inputMultiline": {
                    display: "flex",
                    alignItems: "center",
                    minHeight: "24px",
                  },
                  "& textarea::placeholder": {
                    color: alpha(theme.palette.text.secondary, 0.6),
                    opacity: 1,
                  },
                },
                endAdornment: (
                  <InputAdornment position="end" sx={{ alignSelf: "center" }}>
                    <IconButton
                      aria-label="Send"
                      color="primary"
                      onClick={handleSend}
                      disabled={creating || sending || !message.trim()}
                      sx={{
                        width: 36,
                        height: 36,
                        borderRadius: 2.5,
                        backgroundColor: message.trim()
                          ? theme.palette.primary.main
                          : alpha(theme.palette.text.primary, 0.04),
                        transition: "all 0.25s ease",
                        "&:hover": {
                          backgroundColor: message.trim()
                            ? theme.palette.primary.dark
                            : alpha(theme.palette.text.primary, 0.08),
                          transform: "scale(1.05)",
                        },
                        color: message.trim()
                          ? theme.palette.common.white
                          : alpha(theme.palette.text.secondary, 0.5),
                        "&:disabled": {
                          backgroundColor: alpha(theme.palette.text.primary, 0.02),
                          color: alpha(theme.palette.text.secondary, 0.3),
                        },
                      }}
                    >
                      <SendIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              autoComplete="off"
            />
          </Paper>

          <Stack
            direction="row"
            spacing={1.5}
            flexWrap="wrap"
            alignItems="stretch"
            justifyContent="center"
            sx={{ width: "100%" }}
          >
            {[
              {
                ...TEMPLATES[0],
                icon: <AddTaskIcon fontSize="small" />,
                color: theme.palette.primary.main,
              },
              {
                ...TEMPLATES[1],
                icon: <BugReportIcon fontSize="small" />,
                color: theme.palette.error.main,
              },
              {
                ...TEMPLATES[2],
                icon: <TravelExploreIcon fontSize="small" />,
                color: theme.palette.info.main,
              },
              {
                ...TEMPLATES[3],
                icon: <WarningAmberIcon fontSize="small" />,
                color: theme.palette.warning.main,
              },
            ].map((t, index) => (
              <Fade in timeout={800 + index * 100} key={t.key}>
                <Card
                  {...clickableProps(() => {
                    setMessage(t.text);
                  })}
                  onClick={() => {
                    setMessage(t.text);
                  }}
                  onMouseEnter={() => setHoveredCard(t.key)}
                  onMouseLeave={() => setHoveredCard(null)}
                  elevation={hoveredCard === t.key ? 4 : 0}
                  sx={{
                    cursor: "pointer",
                    px: 1.5,
                    py: 1,
                    borderRadius: 3,
                    minWidth: 200,
                    flex: "1 1 200px",
                    maxWidth: 240,
                    border: `1px solid ${alpha(t.color, hoveredCard === t.key ? 0.3 : 0.15)}`,
                    backgroundColor: alpha(t.color, hoveredCard === t.key ? 0.08 : 0.03),
                    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                    "&:hover": {
                      transform: "translateY(-1px)",
                      borderColor: alpha(t.color, 0.2),
                    },
                  }}
                >
                  <Stack spacing={1} alignItems="center">
                    <Box
                      sx={{
                        color: alpha(t.color, hoveredCard === t.key ? 1 : 0.8),
                        display: "flex",
                        alignItems: "center",
                        transition: "color 0.3s ease",
                      }}
                    >
                      {t.icon}
                    </Box>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 600,
                        textAlign: "center",
                        color: theme.palette.text.primary,
                        fontSize: "0.875rem",
                      }}
                    >
                      {t.label}
                    </Typography>
                  </Stack>
                </Card>
              </Fade>
            ))}
          </Stack>
        </Stack>
      </Fade>
    </Box>
  );
}
